import React from "react";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { API_role_colors } from "../../../constants";
import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import RoleColorPicker from "./RoleColorPicker";

export default function RoleColorsEditor({ companyId, storeId }) {
  const [roles, setRoles] = React.useState(null);
  const [error, setError] = React.useState("");
  const [saving, setSaving] = React.useState(null);
  const [status, setStatus] = React.useState("");
  const [reload, setReload] = React.useState(0);
  const generation = React.useRef(0);
  React.useEffect(() => {
    const current = ++generation.current;
    setRoles(null); setError(""); setStatus(""); setSaving(null);
    getRightworkAPI(API_role_colors, { company_id: companyId, store_id: storeId })
      .then((response) => { if (generation.current === current) setRoles(response.data); })
      .catch(() => { if (generation.current === current) setError("Couldn’t load role colors. Please try again."); });
    return () => { generation.current = current + 1; };
  }, [companyId, storeId, reload]);
  const save = async (role, color) => {
    const current = generation.current;
    setSaving(role.id); setError(""); setStatus("");
    try {
      const response = await postRightworkAPI(API_role_colors, { company_id: companyId, store_id: storeId, role_id: role.id, color });
      if (generation.current !== current) return;
      setRoles(response.data); setStatus(`${role.role_name} color saved.`);
    } catch {
      if (generation.current === current) setError("Couldn’t save this color. Please reload to confirm the saved color before trying again.");
    } finally { if (generation.current === current) setSaving(null); }
  };
  return <div>
    <p className="mb-4 text-xs leading-relaxed text-slate-500">Choose role colors for this location. Departments, roles, and stations are managed by company administrators. Changes save automatically.</p>
    {error && <p role="alert" className="mb-3 rounded-lg bg-rose-50 p-3 text-sm text-rose-700">{error}{<button className="ml-2 underline" onClick={() => setReload((value) => value + 1)}>Try again</button>}</p>}
    <p role="status" className="mb-2 text-xs text-emerald-700">{saving ? "Saving color…" : status}</p>
    {!roles && !error && <div role="status" className="flex justify-center py-4"><span className="sr-only">Loading role colors</span><div aria-hidden="true"><LoadSpinner /></div></div>}
    {roles?.length === 0 && <p className="rounded-lg border border-dashed border-slate-300 p-5 text-sm text-slate-500">No roles yet. A company administrator can add them in Company settings.</p>}
    <div className="divide-y divide-slate-200">
      {roles?.map((role) => <section key={role.id} className="py-4 first:pt-0" aria-label={role.role_name}>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div><h4 className="text-sm font-semibold text-slate-900">{role.role_name}</h4><p className="mt-0.5 text-xs text-slate-500">{role.department}</p></div>
          <span className="text-xs text-slate-500">{role.override_color ? "Location override" : role.company_color ? "Company default" : "No company default set"}</span>
        </div>
        <RoleColorPicker label={`${role.role_name} color`} value={role.color} onChange={(color) => save(role, color)} disabled={saving !== null} />
        {role.override_color && <button type="button" disabled={saving !== null || !role.company_color} title={!role.company_color ? "A company administrator must set a default color first." : undefined} onClick={() => save(role, null)} className="mt-2 rounded text-xs font-medium text-violet-700 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600 disabled:opacity-50">Use company default</button>}
      </section>)}
    </div>
  </div>;
}
